export function init() {
    console.log('[initialized] breadcrumb.ts');

    const breadcrumb = document.querySelector('.breadcrumb');
    if (!breadcrumb) return;

    const container = breadcrumb.querySelector('.breadcrumb__container') as HTMLElement;
    const slider = breadcrumb.querySelector('.breadcrumb__slider') as HTMLElement;
    const list = breadcrumb.querySelector('.breadcrumb__list') as HTMLElement;
    const prevButton = breadcrumb.querySelector('.breadcrumb__nav--prev') as HTMLButtonElement;
    const nextButton = breadcrumb.querySelector('.breadcrumb__nav--next') as HTMLButtonElement;

    if (!container || !slider || !list || !prevButton || !nextButton) return;

    // Desktop breakpoint - 992px
    const DESKTOP_BREAKPOINT = 992;

    function isDesktop() {
        return window.innerWidth > DESKTOP_BREAKPOINT;
    }

    function updateButtonAccessibility() {
        // Skip on desktop
        if (isDesktop()) {
            prevButton.setAttribute('aria-hidden', 'true');
            nextButton.setAttribute('aria-hidden', 'true');
            return;
        }

        const needsSlider = !container.classList.contains('breadcrumb--no-scroll');

        if (needsSlider) {
            prevButton.setAttribute('aria-hidden', 'false');
            nextButton.setAttribute('aria-hidden', 'false');

            // Update button descriptions based on state
            if (prevButton.disabled) {
                prevButton.setAttribute('aria-label', 'Previous breadcrumb items (at beginning)');
            } else {
                prevButton.setAttribute('aria-label', 'Previous breadcrumb items');
            }

            if (nextButton.disabled) {
                nextButton.setAttribute('aria-label', 'Next breadcrumb items (at end)');
            } else {
                nextButton.setAttribute('aria-label', 'Next breadcrumb items');
            }
        } else {
            prevButton.setAttribute('aria-hidden', 'true');
            nextButton.setAttribute('aria-hidden', 'true');
        }
    }

    function scrollToShowItem(item: HTMLElement) {
        // Skip on desktop
        if (isDesktop()) return;

        const needsSlider = !container.classList.contains('breadcrumb--no-scroll');
        if (!needsSlider) return;

        const sliderWidth = slider.offsetWidth;
        const currentScroll = slider.scrollLeft;

        // Get actual button widths
        const prevButtonWidth = prevButton.offsetWidth;
        const nextButtonWidth = nextButton.offsetWidth;

        // Define the visible area (between the buttons)
        const visibleAreaStart = currentScroll + prevButtonWidth;
        const visibleAreaEnd = currentScroll + sliderWidth - nextButtonWidth;

        const itemLeft = item.offsetLeft;
        const itemRight = itemLeft + item.offsetWidth;

        let targetScroll: number | null = null;

        // Check if item is not fully visible and needs scrolling
        if (itemLeft < visibleAreaStart) {
            // Item is cut off on the left - scroll to show it after the prev button
            targetScroll = itemLeft - prevButtonWidth - 15; // 15px padding
        } else if (itemRight > visibleAreaEnd) {
            // Item is cut off on the right - scroll to show it before the next button
            targetScroll = itemRight + nextButtonWidth + 15 - sliderWidth; // 15px padding
        }

        if (targetScroll !== null) {
            // Ensure we don't scroll past the boundaries
            const maxScrollLeft = list.scrollWidth - sliderWidth;
            targetScroll = Math.max(0, Math.min(targetScroll, maxScrollLeft));

            slider.scrollTo({
                left: targetScroll,
                behavior: 'smooth'
            });
        }
    }

    function setupFocusHandlers() {
        // Skip on desktop
        if (isDesktop()) return;

        const breadcrumbItems = list.querySelectorAll('li');
        breadcrumbItems.forEach(item => {
            const focusableElement = item.querySelector('a, span') as HTMLElement;
            if (focusableElement) {
                focusableElement.addEventListener('focus', () => {
                    scrollToShowItem(item as HTMLElement);
                });
            }
        });
    }

    function checkOverflow() {
        // Skip slider functionality on desktop
        if (isDesktop()) {
            container.classList.add('breadcrumb--no-scroll');
            updateButtonAccessibility();
            return;
        }

        // Only check overflow on tablet and mobile
        const sliderWidth = slider.offsetWidth;
        const listWidth = list.scrollWidth;
        const needsSlider = listWidth > sliderWidth;

        if (needsSlider) {
            container.classList.remove('breadcrumb--no-scroll');
            updateButtonStates();
            updateButtonAccessibility();
            setupFocusHandlers();
        } else {
            container.classList.add('breadcrumb--no-scroll');
            updateButtonAccessibility();
        }
    }

    function updateButtonStates() {
        // Skip on desktop
        if (isDesktop()) return;

        const scrollLeft = slider.scrollLeft;
        const maxScrollLeft = slider.scrollWidth - slider.offsetWidth;

        // Update prev button - disable when at start
        if (scrollLeft <= 0) {
            prevButton.disabled = true;
        } else {
            prevButton.disabled = false;
        }

        // Update next button - disable when at end
        if (scrollLeft >= maxScrollLeft - 1) { // -1 for rounding issues
            nextButton.disabled = true;
        } else {
            nextButton.disabled = false;
        }

        // Update accessibility after state changes
        updateButtonAccessibility();
    }

    function scrollToPosition(direction: 'left' | 'right') {
        // Skip on desktop
        if (isDesktop()) return;

        const items = Array.from(list.children) as HTMLElement[];
        if (items.length === 0) return;

        const sliderWidth = slider.offsetWidth;
        const currentScroll = slider.scrollLeft;

        // Get actual button widths
        const prevButtonWidth = prevButton.offsetWidth;
        const nextButtonWidth = nextButton.offsetWidth;

        // Define the visible area (between the buttons)
        const visibleAreaStart = currentScroll + prevButtonWidth;
        const visibleAreaEnd = currentScroll + sliderWidth - nextButtonWidth;
        const visibleAreaWidth = visibleAreaEnd - visibleAreaStart;

        // Find the first fully visible item or the first item that's partially visible
        let currentVisibleIndex = 0;
        for (let i = 0; i < items.length; i++) {
            const item = items[i] as HTMLElement;
            const itemLeft = item.offsetLeft;
            const itemRight = itemLeft + item.offsetWidth;

            // Check if item is at least partially visible in the center area
            if (itemRight > visibleAreaStart && itemLeft < visibleAreaEnd) {
                currentVisibleIndex = i;
                break;
            }
        }

        let targetIndex: number;
        if (direction === 'right') {
            // Move to next item
            targetIndex = Math.min(currentVisibleIndex + 1, items.length - 1);
        } else {
            // Move to previous item
            targetIndex = Math.max(currentVisibleIndex - 1, 0);
        }

        const targetItem = items[targetIndex] as HTMLElement;
        let targetScroll: number;

        if (direction === 'right') {
            // When going right, position the item so its left edge is visible after the prev button
            targetScroll = targetItem.offsetLeft - prevButtonWidth - 10; // 10px padding
        } else {
            // When going left, position the item so its left edge is visible after the prev button
            targetScroll = targetItem.offsetLeft - prevButtonWidth - 10; // 10px padding
        }

        // Ensure we don't scroll past the boundaries
        const maxScrollLeft = list.scrollWidth - sliderWidth;
        targetScroll = Math.max(0, Math.min(targetScroll, maxScrollLeft));

        slider.scrollTo({
            left: targetScroll,
            behavior: 'smooth'
        });
    }

    // Event listeners
    prevButton.addEventListener('click', () => scrollToPosition('left'));
    nextButton.addEventListener('click', () => scrollToPosition('right'));

    // Update button states on scroll (only on tablet/mobile)
    slider.addEventListener('scroll', updateButtonStates);

    // Handle window resize
    function handleResize() {
        checkOverflow();
    }

    window.addEventListener('resize', handleResize);

    // Initialize
    checkOverflow();

    // Auto-scroll to last element (current page) on page load
    function scrollToLastElement() {
        // Skip on desktop
        if (isDesktop()) return;

        const needsSlider = !container.classList.contains('breadcrumb--no-scroll');
        if (!needsSlider) return;

        const items = Array.from(list.children) as HTMLElement[];
        if (items.length === 0) return;

        // Get the last element (current page)
        const lastItem = items[items.length - 1] as HTMLElement;
        
        // Wait a bit for the layout to settle, then scroll to the last element
        setTimeout(() => {
            scrollToShowItem(lastItem);
        }, 100);
    }

    // Call scrollToLastElement after initialization
    setTimeout(scrollToLastElement, 50);

    // Handle dynamic content changes (only on tablet/mobile)
    const observer = new ResizeObserver(() => {
        checkOverflow();
    });

    observer.observe(list);
}